import { useState } from 'react'
import { authClient } from './authClient'
import { useAuthStore } from './authStore'

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const error = useAuthStore((s) => s.error)
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated)
  const setError = useAuthStore((s) => s.setError)

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const r = await authClient.login(email, password)
      setAuthenticated({
        accessToken: r.access_token,
        expiresIn: r.expires_in,
        user: r.user,
      })
    } catch (err) {
      const status = (err as { status?: number })?.status
      setError(status === 401 ? 'Invalid email or password.' : 'Login failed. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--bg-0, #0b0d12)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <form
        onSubmit={onSubmit}
        style={{
          width: '100%',
          maxWidth: 380,
          background: 'var(--bg-1, #14171f)',
          border: '0.5px solid var(--bd-0, #2b2f3a)',
          borderRadius: 12,
          padding: 28,
          boxShadow: '0 10px 40px rgba(0,0,0,0.45)',
          color: 'var(--t-0, #e8ebf2)',
        }}
      >
        <div style={{ fontSize: 13, color: 'var(--t-3, #8a92a6)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
          VMS Dashboard
        </div>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 18px 0' }}>Sign in</h1>

        <label style={{ display: 'block', fontSize: 12, color: 'var(--t-2, #b4bbcd)', marginBottom: 6 }}>Email</label>
        <input
          type="email"
          required
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          autoComplete="username"
          style={inputStyle}
        />

        <label style={{ display: 'block', fontSize: 12, color: 'var(--t-2, #b4bbcd)', margin: '14px 0 6px' }}>Password</label>
        <input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          style={inputStyle}
        />

        {error && (
          <div
            style={{
              fontSize: 12,
              color: 'var(--crit, #ff6b6b)',
              background: 'var(--crit-bg, rgba(255,107,107,0.08))',
              border: '0.5px solid var(--crit-bd, rgba(255,107,107,0.35))',
              borderRadius: 6,
              padding: '8px 10px',
              marginTop: 14,
            }}
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !email || !password}
          style={{
            marginTop: 18,
            width: '100%',
            padding: '10px 14px',
            background: submitting ? 'var(--bg-3, #2b2f3a)' : 'var(--info, #4f8cff)',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 500,
            cursor: submitting ? 'not-allowed' : 'pointer',
            opacity: submitting || !email || !password ? 0.7 : 1,
            transition: 'opacity 0.15s',
          }}
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>

        <div style={{ marginTop: 18, fontSize: 11, color: 'var(--t-3, #8a92a6)', lineHeight: 1.45 }}>
          Use the seeded local accounts:
          <br />
          <code style={codeStyle}>admin@vms.local</code> / <code style={codeStyle}>admin123!</code>
          <br />
          <code style={codeStyle}>user@vms.local</code> / <code style={codeStyle}>user123!</code>
        </div>
      </form>
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 11px',
  background: 'var(--bg-2, #1b1f2a)',
  border: '0.5px solid var(--bd-0, #2b2f3a)',
  borderRadius: 6,
  color: 'var(--t-0, #e8ebf2)',
  fontSize: 13,
  outline: 'none',
  boxSizing: 'border-box',
}

const codeStyle: React.CSSProperties = {
  background: 'var(--bg-2, #1b1f2a)',
  padding: '1px 6px',
  borderRadius: 3,
  fontSize: 11,
}
